import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { Award, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';

export default function BadgesPage() {
  const { api } = useAuth();
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLearners();
  }, []);

  useEffect(() => {
    if (selectedLearner) {
      fetchBadges();
    }
  }, [selectedLearner]);

  const fetchLearners = async () => {
    try {
      const response = await api.get('/learners/');
      // Handle both paginated (Django) and non-paginated responses
      const learnersList = response.data?.results || response.data || [];
      setLearners(learnersList);
      if (learnersList.length > 0) {
        setSelectedLearner(learnersList[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch learners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const response = await api.get(`/badges/learner/${selectedLearner}`);
      // Handle both paginated (Django) and non-paginated responses
      setBadges(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
      setBadges([]);
    }
  };

  const selectedLearnerData = learners.find(l => l.id === selectedLearner);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="badges-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Badges & Certificates</h1>
          <p className="text-muted-foreground">View earned achievements and certificates</p>
        </div>

        {learners.length > 0 && (
          <Select value={selectedLearner} onValueChange={setSelectedLearner}>
            <SelectTrigger className="w-[200px]" data-testid="learner-select">
              <SelectValue placeholder="Select learner" />
            </SelectTrigger>
            <SelectContent>
              {learners.map(learner => (
                <SelectItem key={learner.id} value={learner.id}>
                  {learner.first_name} {learner.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {learners.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <Award className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No learners yet</h2>
            <p className="text-muted-foreground">Add a learner to track their badges.</p>
          </CardContent>
        </Card>
      ) : badges.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <Award className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No badges yet</h2>
            <p className="text-muted-foreground">
              {selectedLearnerData?.first_name} hasn't earned any badges yet. Complete courses to earn badges!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {badges.map((item, index) => (
            <Card key={item.id} className="card-marketing" data-testid={`badge-${index}`}>
              <CardContent className="p-6 text-center">
                <div className="badge-earned w-20 h-20 mx-auto mb-4">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.badge_name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <Award className="w-10 h-10 text-white" />
                  )}
                </div>
                <h3 className="font-heading text-lg font-bold mb-1">{item.badge_name}</h3>
                <p className="text-sm text-muted-foreground mb-3">{item.description || 'Achievement badge'}</p>
                
                <div className="space-y-2">
                  <Badge variant="secondary" className="capitalize">
                    {item.badge_type?.replace(/_/g, ' ') || 'Achievement'}
                  </Badge>
                  <p className="text-xs text-muted-foreground">
                    Earned on {new Date(item.issued_at).toLocaleDateString()}
                  </p>
                  <p className="text-xs font-mono text-muted-foreground">
                    Verification: {item.verification_code}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
